import Qs from 'qs';
import { getAuth } from "firebase/auth";
import { useFirebaseApp } from 'vuefire';
import { setDoc, doc } from 'firebase/firestore';
import { useFirestore } from 'vuefire';
import { jwtDecode } from "jwt-decode";
import { useUserStore } from '@/stores/user';
import { useConnectivityStore } from '@/stores/connectivityStore';

export default defineNuxtPlugin(nuxtApp => {
  const urlPrefix = '/api/';
  let registerPromise = null;

  const assertOnline = () => {
    if (!useConnectivityStore().isOnline) {
      throw new Error('offline');
    }
  };

  const getFirebaseAuth = () => {
    const auth = getAuth(useFirebaseApp());
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    return auth;
  };

  const makeService = (serviceName, baseUrl) => ({
    async request({ url, method = 'GET', params = {}, headers = {}, body = null }) {
      assertOnline();
      const auth = getFirebaseAuth();
      const token = await auth.currentUser.getIdToken();

      const fullUrl = new URL(urlPrefix + url, baseUrl);
      fullUrl.search = Qs.stringify(params, {
        arrayFormat: "indices",
        encode: false
      });

      const fetchOptions = {
        method,
        headers: {
          ...headers,
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };

      if (body) {
        fetchOptions.body = JSON.stringify(body);
        fetchOptions.headers['Content-Type'] = 'application/json';
      }

      try {
        const response = await fetch(fullUrl, fetchOptions);
        if (!response.ok) throw response;
        if (response.status === 204) return null;
        return await response.json();
      } catch (error) {
        throw error;
      }
    }
  });

  const cloud = {
    services: {
      cloud: makeService('cloud', useRuntimeConfig().public.cloudUrl),
      auth: makeService('auth', useRuntimeConfig().public.authUrl),
      search: makeService('search', useRuntimeConfig().public.searchUrl)
    },
    service(name = 'cloud') {
      return this.services[name];
    },
    registerUser(token, body) {
      return $fetch(useRuntimeConfig().public.authUrl + '/api/user', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body,
      });
    },
    async ensureUserRegistered() {
      const auth = getFirebaseAuth();
      const token = await auth.currentUser.getIdToken();
      const decoded = jwtDecode(token);

      if (decoded.user) {
        return;
      }

      // Reuse in-flight registration to avoid concurrent calls
      if (registerPromise) {
        return registerPromise;
      }

      registerPromise = (async () => {
        try {
          const { $helpers } = nuxtApp;
          const browserLang = navigator.language || navigator.languages[0];
          const langCode = browserLang.substring(0, 2).toLowerCase();
          const supportedLanguages = ['en', 'pt', 'es'];
          const languageCode = supportedLanguages.includes(langCode)
            ? langCode
            : ($helpers.getConfigurationKey('languageCode') || 'en');

          const userData = {
            created_by_organization_id: $helpers.getConfigurationKey('organizationId'),
            language_code: languageCode,
          };

          if (auth.currentUser.photoURL) {
            userData.picture_url = auth.currentUser.photoURL;
          }

          await this.registerUser(token, userData);

          // Force token refresh so subsequent calls have the .user claim
          const freshToken = await auth.currentUser.getIdToken(true);
          const freshDecoded = jwtDecode(freshToken);
          const userStore = useUserStore();
          userStore.setUser({
            ...userStore.user,
            userId: freshDecoded.user?.id,
          });
        } finally {
          registerPromise = null;
        }
      })();

      return registerPromise;
    },
    async createMlsRequest(endpoint, data) {
      assertOnline();
      await this.ensureUserRegistered();

      const auth = getFirebaseAuth();
      const db = useFirestore();
      const id = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString();
      await setDoc(doc(db, 'services/mls/requests', id), {
        endpoint,
        user: { email: auth.currentUser.email },
        data,
      });
    }
  };

  nuxtApp.provide('cloud', cloud);
});